import React, { useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated, selectUser } from '../../store/slices/authSlice';
import { usePermissions } from '../../contexts/PermissionContext';

const ProtectedRoute = ({ children, requireAuth = true, requireAdmin = false, requireAdminOrManager = false, requiredPermission = null }) => {
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const { hasPermission, canAccessAdminDashboard } = usePermissions();

  const isMobileNamespace = (location?.pathname || '').startsWith('/mobile');
  const loginPath = isMobileNamespace ? '/mobile/login' : '/login';

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Check admin requirement
  if (requireAdmin && (!isAuthenticated || (user?.role !== 'admin' && user?.role !== 'administrator'))) {
    console.debug('[ProtectedRoute] Admin access denied:', { 
      isAuthenticated, 
      user: user?.role, 
      canAccessDashboard: canAccessAdminDashboard() 
    });
    if (isAuthenticated) {
      // Use setTimeout to avoid setState during render
      setTimeout(() => toast.error('Permission denied: admin access required'), 0);
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to={loginPath} replace />;
  }
  
  // Check admin/manager requirement
  if (requireAdminOrManager) {
    const isAdmin = user?.role === 'admin' || user?.role === 'administrator';
    const isManager = user?.role === 'manager';
    const hasAccess = isAdmin || (isManager && canAccessAdminDashboard());
    
    if (!isAuthenticated || !hasAccess) {
      console.debug('[ProtectedRoute] Admin/Manager access denied:', { 
        isAuthenticated, 
        userRole: user?.role, 
        isAdmin,
        isManager,
        canAccessDashboard: canAccessAdminDashboard()
      });
      if (isAuthenticated) {
        // Use setTimeout to avoid setState during render
        setTimeout(() => toast.error('Permission denied: insufficient privileges'), 0);
        return <Navigate to="/admin" replace />;
      }
      return <Navigate to={loginPath} replace />;
    }
    
    console.debug('[ProtectedRoute] Admin/Manager access granted:', { 
      isAuthenticated, 
      userRole: user?.role,
      isAdmin,
      isManager,
      canAccessDashboard: canAccessAdminDashboard() 
    });
  }

  // Check specific permission requirement
  if (requiredPermission) {
    const allowed = isAuthenticated && hasPermission(requiredPermission);
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[ProtectedRoute] Permission check:', {
        requiredPermission,
        isAuthenticated,
        userRole: user?.role,
        allowed
      });
    }
    if (!allowed) {
      if (isAuthenticated) {
        // Use setTimeout to avoid setState during render
        setTimeout(() => toast.error('Permission denied: you do not have access to this page'), 0);
        return <Navigate to="/admin" replace />;
      }
      return <Navigate to={loginPath} replace />;
    }
  }

  // Block customers from admin routes
  // Retain block for customers (optional, but keep logic unchanged)
    if (requireAdmin && user?.role === 'customer') {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
